import { api, throwApiError } from "../../../api/api";
import type { DpDeductionCalcMode, DpPayrollItemKind } from "./payrollApi";

/**
 * Benefícios personalizáveis (plano odontológico, Gympass…): catálogo em
 * Cadastros → Benefícios e atribuição por contrato na ficha. VT, VR, VA e
 * adiantamento continuam nas opções de benefício (`contractsApi`), porque têm
 * regra legal própria na folha.
 *
 * `Decimal` do Prisma chega como STRING (BUG-R05): converter com `Number()`.
 */

export type DpBenefitTypeItem = { id: string; code: string; name: string; kind: DpPayrollItemKind; isActive: boolean };

export type DpBenefitType = {
  id: string;
  name: string;
  description: string | null;
  itemId: string | null;
  item: DpBenefitTypeItem | null;
  defaultEmployerCost: string | null;
  defaultEmployeeAmount: string | null;
  sortOrder: number;
  isActive: boolean;
  /** Colaboradores com o benefício (atribuições não excluídas). Só na listagem. */
  assignedCount?: number;
};

export type BenefitTypePayload = {
  name?: string;
  description?: string | null;
  itemId?: string | null;
  defaultEmployerCost?: number | null;
  defaultEmployeeAmount?: number | null;
  sortOrder?: number;
  isActive?: boolean;
};

export type DpContractBenefit = {
  id: string;
  contractId: string;
  benefitTypeId: string;
  benefitType: { id: string; name: string; isActive: boolean; item: { id: string; code: string; name: string } | null };
  startMonth: string;
  endMonth: string | null;
  employerCost: string | null;
  calcMode: DpDeductionCalcMode | null;
  percent: string | null;
  amount: string | null;
  notes: string | null;
};

export type ContractBenefitPayload = {
  benefitTypeId: string;
  startMonth: string;
  endMonth?: string | null;
  employerCost?: number | null;
  calcMode?: DpDeductionCalcMode | null;
  percent?: number | null;
  amount?: number | null;
  notes?: string | null;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

async function call<T>(fallback: string, run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
}

// Catálogo
export const listBenefitTypesApi = (token: string) =>
  call<DpBenefitType[]>("Erro ao carregar os benefícios.", () => api.get("/dp/benefit-types", auth(token)));
export const createBenefitTypeApi = (token: string, payload: BenefitTypePayload) =>
  call<DpBenefitType>("Erro ao cadastrar o benefício.", () => api.post("/dp/benefit-types", payload, auth(token)));
export const updateBenefitTypeApi = (token: string, id: string, payload: BenefitTypePayload) =>
  call<DpBenefitType>("Erro ao salvar o benefício.", () => api.patch(`/dp/benefit-types/${id}`, payload, auth(token)));

// Do colaborador
export const listContractBenefitsApi = (token: string, contractId: string) =>
  call<DpContractBenefit[]>("Erro ao carregar os benefícios do colaborador.", () =>
    api.get(`/dp/contracts/${contractId}/benefits`, auth(token))
  );
export const createContractBenefitApi = (token: string, contractId: string, payload: ContractBenefitPayload) =>
  call<DpContractBenefit>("Erro ao atribuir o benefício.", () =>
    api.post(`/dp/contracts/${contractId}/benefits`, payload, auth(token))
  );
export const updateContractBenefitApi = (token: string, id: string, payload: ContractBenefitPayload) =>
  call<DpContractBenefit>("Erro ao salvar o benefício.", () => api.put(`/dp/contract-benefits/${id}`, payload, auth(token)));
export const deleteContractBenefitApi = (token: string, id: string) =>
  call<void>("Erro ao excluir o benefício.", () => api.delete(`/dp/contract-benefits/${id}`, auth(token)));
